import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { theme, animations } from '../../styles/theme';
import { useAuth } from '../../hooks/useApi';
import ApiService from '../../services/ApiService';
import SpinWheelService from '../../services/SpinWheelService';

const sparkleAnimation = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
`;

const gentlePulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.06); opacity: 1; }
`;

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalSlideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.92); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const SpinWheelContainer = styled.div`
  min-height: ${props => props.$embedded ? 'auto' : '100vh'};
  background: ${props => props.$embedded ? 'transparent' : theme.gradients.background};
  position: relative;
  overflow: ${props => props.$embedded ? 'visible' : 'hidden'};
 
  ${props => !props.$embedded && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
      pointer-events: none;
      z-index: 0;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: ${theme.colors.backgroundStars};
      background-size: 25% 25%;
      pointer-events: none;
      background-repeat: repeat;
      animation: ${animations.sparkle} 4s ease-in-out infinite;
      z-index: 0;
    }
  `}
`;

const ContentWrapper = styled.div`
  max-width: ${props => props.$embedded ? '100%' : '1400px'};
  width: 100%;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '0' : theme.spacing.xl};
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 1200px) {
    padding: ${theme.spacing.lg};
  }
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.sm};
  }
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$embedded ? '1fr' : '1fr 400px'};
  gap: ${theme.spacing.xl};
  justify-content: center;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
  width: 100%;
  align-items: center;
`;

const WheelSection = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.card};
  padding: ${props => props.$compact ? theme.spacing.md : (props.$embedded ? theme.spacing.lg : theme.spacing.xxl)};
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const WheelTitle = styled.h1`
  color: ${theme.colors.textPrimary};
  font-size: ${props => props.$compact ? theme.typography.fontSizes.xl : theme.typography.fontSizes.xxl};
  margin: 0 0 ${props => props.$compact ? theme.spacing.xs : theme.spacing.md} 0;
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

   @media (max-width: 768px) {
   font-size: 1.9rem;
   
  }

`;

const WheelSubtitle = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${props => props.$compact ? theme.typography.fontSizes.sm : theme.typography.fontSizes.md};
  margin: 0 0 ${props => props.$compact ? theme.spacing.md : theme.spacing.xl} 0;
`;

const WheelContainer = styled.div`
  position: relative;
  width: ${props => props.$compact ? '340px' : '500px'};
  height: ${props => props.$compact ? '340px' : '500px'};
  margin: 0 auto ${props => props.$compact ? theme.spacing.md : theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1200px;
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    width: ${props => props.$compact ? '370px' : '540px'};
    height: ${props => props.$compact ? '370px' : '540px'};
    border-radius: 50%;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%);
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 105%;
    height: 105%;
    border-radius: 50%;
    box-shadow: inset 0 0 60px rgba(0, 0, 0, 0.6),
                0 15px 50px rgba(0, 0, 0, 0.4);
    pointer-events: none;
    z-index: 3;
  }

  @media (max-width: 768px) {
    width: ${props => props.$compact ? '300px' : '350px'};
    height: ${props => props.$compact ? '300px' : '350px'};
    
    &::before {
      width: ${props => props.$compact ? '330px' : '380px'};
      height: ${props => props.$compact ? '330px' : '380px'};
    }
  }
`;

// Wheel of Fortune style fixed needle/pointer at top
const WheelPointer = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  pointer-events: none;
  
  /* Main needle shaft pointing down into wheel */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 80px;
    background: linear-gradient(180deg, #ff0000 0%, #cc0000 100%);
    border-radius: 4px;
    box-shadow: 0 4px 15px rgba(255, 0, 0, 0.6),
                inset 2px 0 5px rgba(255, 255, 255, 0.3),
                inset -2px 0 5px rgba(0, 0, 0, 0.3);
  }
  
  /* Arrow tip pointing at segment */
  &::after {
    content: '';
    position: absolute;
    top: 75px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    border-top: 25px solid #ff0000;
    filter: drop-shadow(0 3px 8px rgba(255, 0, 0, 0.7));
  }
`;

// Mounting bracket for needle (like real Wheel of Fortune)
const NeedleBracket = styled.div`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 50%, #ffd700 100%);
  border-radius: 50%;
  border: 5px solid #000;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.5),
              inset 0 3px 10px rgba(255, 255, 255, 0.4),
              inset 0 -3px 10px rgba(0, 0, 0, 0.3);
  z-index: 25;
  pointer-events: none;
  
  /* Center bolt */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 30px;
    background: radial-gradient(circle, #333 0%, #000 100%);
    border-radius: 50%;
    box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.8);
  }
  
  /* Shine effect */
  &::after {
    content: '';
    position: absolute;
    top: 15%;
    left: 15%;
    width: 25px;
    height: 25px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.6) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const WheelCanvas = styled.svg`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 15px 40px rgba(0, 0, 0, 0.5)) brightness(1.05) contrast(1.08);
  transform-origin: center center;
  border-radius: 50%;
  position: relative;
  z-index: 2;
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: rotate(${props => props.$rotation}deg) translateZ(0);
  transition: ${props => props.$isSpinning
    ? `transform ${props.$duration}ms cubic-bezier(0.12, 0.75, 0.28, 0.99)`
    : 'none'};

  ${props => props.$isSpinning && css`
    filter: drop-shadow(0 15px 40px rgba(0, 0, 0, 0.5))
            brightness(1.1) contrast(1.1);
  `}
`;

const CenterCircle = styled.button`
  position: absolute;
  width: ${props => {
    if (props.$isSpinning) return props.$compact ? '102px' : '124px';
    return props.$compact ? '82px' : '100px';
  }};
  height: ${props => {
    if (props.$isSpinning) return props.$compact ? '102px' : '124px';
    return props.$compact ? '82px' : '100px';
  }};
  border: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  background: ${props => {
    if (props.$disabled) return 'linear-gradient(135deg, #5f5f5f 0%, #3f3f3f 100%)';
    if (props.$mode === 'buy') return 'linear-gradient(135deg, #ff4d4d 0%, #b30000 100%)';
    return theme.gradients.primary;
  }};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => {
    if (props.$isSpinning) return props.$compact ? '0.68rem' : '0.78rem';
    return props.$compact ? '1.05rem' : '1.22rem';
  }};
  line-height: 1;
  white-space: nowrap;
  letter-spacing: ${props => props.$isSpinning ? '0.01em' : '0.03em'};
  font-weight: bold;
  color: ${props => props.$disabled ? '#cfcfcf' : (props.$mode === 'buy' ? '#fff' : '#000')};
  box-shadow: ${theme.shadows.button};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  z-index: 5;
  opacity: ${props => props.$disabled ? 0.8 : 1};

  &:hover {
    transform: ${props => props.$disabled ? 'scale(1)' : 'scale(1.06)'};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.button};
  }

  ${props => props.$isSpinning && css`
    animation: ${gentlePulse} 1.4s ease-in-out infinite;
  `}
`;

const AttemptsInfo = styled.div`
width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  margin-bottom: ${props => props.$compact ? theme.spacing.md : theme.spacing.lg};
  padding: ${props => props.$compact ? theme.spacing.md : theme.spacing.lg};
  background: rgba(102, 126, 234, 0.1);
  border-radius: ${theme.borderRadius.medium};
  box-shadow: inset 0 2px 10px rgba(102, 126, 234, 0.2);
  border: 1px solid rgba(102, 126, 234, 0.3);
`;

const AttemptsLabel = styled.div`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const AttemptsCount = styled.span`
  color: ${theme.colors.primary};
  font-size: ${theme.typography.fontSizes.xl};
  font-weight: bold;
`;

const BuyConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  z-index: 9998;
  animation: ${overlayFadeIn} 0.2s ease-out;
`;

const BuyConfirmCard = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: ${theme.spacing.lg};
  width: 90vw;
  max-width: 420px;
  background: rgba(18, 18, 38, 0.97);
  border: 1px solid rgba(102, 126, 234, 0.5);
  border-radius: ${theme.borderRadius.large};
  color: ${theme.colors.textPrimary};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 30px rgba(102, 126, 234, 0.15);
  z-index: 9999;
  animation: ${modalSlideUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  text-align: center;
`;

const BuyConfirmTitle = styled.div`
  font-size: ${theme.typography.fontSizes.md};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const BuyConfirmText = styled.div`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
`;

const BuyConfirmActions = styled.div`
  margin-top: ${theme.spacing.md};
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: center;
`;

const BuyConfirmButton = styled.button`
  border: none;
  padding: 10px 18px;
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${props => props.$variant === 'confirm'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'rgba(255,255,255,0.12)'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

/* ---- Celebration Overlay ---- */
const celebrateIn = keyframes`
  0% { opacity: 0; transform: scale(0.3) rotate(-10deg); }
  50% { opacity: 1; transform: scale(1.08) rotate(2deg); }
  70% { transform: scale(0.96) rotate(-1deg); }
  100% { opacity: 1; transform: scale(1) rotate(0); }
`;

const celebrateOut = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.6) translateY(40px); }
`;

const confettiFall = keyframes`
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

const burstOut = keyframes`
  0% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 1; }
  60% { opacity: 1; }
  100% { transform: translate(calc(var(--tx) * 5), calc(var(--ty) * 5)) scale(0); opacity: 0; }
`;

const sparkleFlash = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  20% { transform: scale(1.2) rotate(90deg); opacity: 1; }
  40% { transform: scale(0.8) rotate(180deg); opacity: 1; }
  60% { transform: scale(1.4) rotate(270deg); opacity: 0.8; }
  80% { transform: scale(0.6) rotate(360deg); opacity: 0.4; }
  100% { transform: scale(0) rotate(450deg); opacity: 0; }
`;

const starPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  30% { transform: scale(1.3); opacity: 1; }
  60% { transform: scale(0.9); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const ringExpand = keyframes`
  0% { transform: scale(0); opacity: 0.8; border-width: 4px; }
  100% { transform: scale(3); opacity: 0; border-width: 1px; }
`;

const fireworkTrail = keyframes`
  0% { height: 0; bottom: -10%; opacity: 1; }
  40% { height: 30%; bottom: 20%; opacity: 1; }
  50% { height: 4px; bottom: 50%; opacity: 1; }
  51% { height: 0; opacity: 0; }
  100% { opacity: 0; }
`;

const fireworkBurst = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { opacity: 1; }
  51% { transform: scale(0); opacity: 1; }
  70% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
`;

const CelebrationBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${props => props.$closing ? celebrateOut : animations.fadeIn} ${props => props.$closing ? '0.35s' : '0.3s'} ease forwards;
  cursor: pointer;
`;

const CelebrationCard = styled.div`
  background: none;
  border: none;
  padding: 2rem;
  text-align: center;
  max-width: 400px;
  width: 90vw;
  position: relative;
  animation: ${celebrateIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  overflow: visible;
`;

const ConfettiPiece = styled.div`
  position: fixed;
  width: ${props => props.$size || 10}px;
  height: ${props => props.$size || 10}px;
  background: ${props => props.$color};
  top: -20px;
  left: ${props => props.$left}%;
  border-radius: ${props => props.$round ? '50%' : '2px'};
  animation: ${confettiFall} ${props => props.$duration || 2.5}s ease-in ${props => props.$delay || 0}s forwards;
  z-index: 10000;
  pointer-events: none;
`;

/* Firework burst container */
const FireworkBurstArea = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  margin: 0 auto 0.5rem;
`;

/* Central icon */
const CenterStar = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  font-weight: 200;
  color: #e8d48b;
  animation: ${starPop} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both;
  z-index: 3;
`;

/* Expanding ring pulses */
const BurstRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  border-radius: 50%;
  border: 3px solid ${props => props.$color || 'rgba(255, 215, 0, 0.7)'};
  animation: ${ringExpand} 1.2s ease-out ${props => props.$delay || '0.3s'} both;
  pointer-events: none;
`;

/* Sparkle particles that burst outward */
const SparkleParticle = styled.div`
  position: absolute;
  width: ${props => props.$size || 6}px;
  height: ${props => props.$size || 6}px;
  background: ${props => props.$color || '#FFD700'};
  border-radius: 50%;
  top: 50%;
  left: 50%;
  margin: -3px 0 0 -3px;
  --tx: ${props => props.$tx || '0px'};
  --ty: ${props => props.$ty || '0px'};
  animation: ${burstOut} ${props => props.$dur || '1s'} ease-out ${props => props.$delay || '0.35s'} both;
  box-shadow: 0 0 6px ${props => props.$color || '#FFD700'};
  pointer-events: none;
`;

/* Small cross-shaped sparkle */
const SparkleIcon = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  top: ${props => props.$top};
  left: ${props => props.$left};
  animation: ${sparkleFlash} ${props => props.$dur || '1.2s'} ease-in-out ${props => props.$delay || '0s'} both;
  pointer-events: none;
  z-index: 2;

  &::before, &::after {
    content: '';
    position: absolute;
    background: ${props => props.$color || '#FFD700'};
    border-radius: 2px;
    box-shadow: 0 0 4px ${props => props.$color || '#FFD700'};
  }

  &::before {
    width: 100%;
    height: 2px;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }

  &::after {
    width: 2px;
    height: 100%;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
  }
`;

/* Firework trail line */
const FireworkTrailLine = styled.div`
  position: absolute;
  width: 2px;
  bottom: -10%;
  left: ${props => props.$left || '50%'};
  background: linear-gradient(to top, transparent, ${props => props.$color || '#FFD700'});
  animation: ${fireworkTrail} 1s ease-out ${props => props.$delay || '0s'} both;
  pointer-events: none;
  border-radius: 1px;
  z-index: 1;
`;

/* Firework starburst SVG effect */
const FireworkStarburst = styled.div`
  position: absolute;
  top: ${props => props.$top || '50%'};
  left: ${props => props.$left || '50%'};
  width: 80px;
  height: 80px;
  margin: -40px 0 0 -40px;
  border-radius: 50%;
  background: radial-gradient(circle, ${props => props.$color || 'rgba(255, 215, 0, 0.4)'} 0%, transparent 70%);
  animation: ${fireworkBurst} 1.4s ease-out ${props => props.$delay || '0.5s'} both;
  pointer-events: none;
  z-index: 1;
`;

/* Data for burst particles */
const BURST_PARTICLES = [
  { tx: '20px',  ty: '-30px', color: '#FFD700', size: 7, dur: '1.1s', delay: '0.3s' },
  { tx: '-25px', ty: '-25px', color: '#FF6B6B', size: 5, dur: '1.0s', delay: '0.35s' },
  { tx: '30px',  ty: '10px',  color: '#4ECDC4', size: 6, dur: '1.2s', delay: '0.32s' },
  { tx: '-20px', ty: '25px',  color: '#FFEAA7', size: 5, dur: '0.9s', delay: '0.38s' },
  { tx: '10px',  ty: '30px',  color: '#FF8C00', size: 7, dur: '1.1s', delay: '0.34s' },
  { tx: '-30px', ty: '5px',   color: '#DDA0DD', size: 5, dur: '1.0s', delay: '0.36s' },
  { tx: '25px',  ty: '-15px', color: '#45B7D1', size: 6, dur: '1.15s', delay: '0.33s' },
  { tx: '-10px', ty: '-35px', color: '#FF69B4', size: 5, dur: '0.95s', delay: '0.37s' },
  { tx: '35px',  ty: '-5px',  color: '#FFD700', size: 8, dur: '1.05s', delay: '0.31s' },
  { tx: '-35px', ty: '-15px', color: '#96CEB4', size: 5, dur: '1.1s', delay: '0.39s' },
  { tx: '15px',  ty: '35px',  color: '#FF6B6B', size: 6, dur: '1.0s', delay: '0.35s' },
  { tx: '-15px', ty: '30px',  color: '#00CED1', size: 5, dur: '1.2s', delay: '0.33s' },
];

const CelebrationTitle = styled.h2`
  color: #e8d48b;
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 0.35rem;
  letter-spacing: 0.02em;
`;

const CelebrationSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;
  margin: 0 0 1.25rem;
`;

const CelebrationAmount = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: #fff;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  span {
    color: #e8d48b;
  }
`;

const CelebrationBalance = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.95rem;
  margin-top: 1.25rem;
`;

const CelebrationDismiss = styled.button`
  margin-top: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.65rem 2.5rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.03em;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }
`;

const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF8C00', '#00CED1', '#FF69B4'];
const CHAT_MESSAGE_RETENTION_HOURS = 24;
const CHAT_PURGE_INTERVAL_MS = 5 * 60 * 1000;

const WinnerLogWrap = styled.div`
  width: 100%;
  margin-top: ${theme.spacing.md};
  max-height: 160px;
  overflow-y: auto;
  border-radius: ${theme.borderRadius.medium};
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 2px; }
`;

const WinnerLogTitle = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.25);
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  position: sticky;
  top: 0;
  background: rgba(8, 8, 8, 0.96);
  z-index: 1;
`;

const WinnerLogEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  animation: ${animations.fadeIn} 0.3s ease-out;
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);

  &:last-child { border-bottom: none; }
`;

const WinnerLogDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #e8d48b;
  flex-shrink: 0;
  opacity: 0.6;
`;

const WinnerLogName = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  font-size: 0.78rem;
  flex-shrink: 0;
`;

const WinnerLogText = styled.span`
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.75rem;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WinnerLogTime = styled.span`
  color: rgba(255, 255, 255, 0.15);
  font-size: 0.65rem;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

const ChatSection = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.card};
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  height: 700px;

  @media (max-width: 1024px) {
    height: 400px;
  }
`;

const ChatTitle = styled.h2`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.lg};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.md};
  background: rgba(0, 0, 0, 0.2);
  border-radius: ${theme.borderRadius.medium};
  margin-bottom: ${theme.spacing.md};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 4px;
  }
`;

const ChatMessage = styled.div`
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.$isWin ? 'rgba(255, 215, 0, 0.1)' : 'transparent'};
  border-left: 3px solid ${props => props.$isWin ? theme.colors.primary : 'transparent'};
  border-radius: ${theme.borderRadius.small};
  animation: ${animations.fadeIn} 0.3s ease-out;
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const MessageUser = styled.span`
  color: ${theme.colors.primary};
  font-weight: bold;
  font-size: ${theme.typography.fontSizes.sm};
`;

const MessageTime = styled.span`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.xs};
`;

const MessageContent = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.5;
`;

const WinAnnouncement = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.primary};
  font-weight: bold;
`;

const ChatInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ChatInput = styled.input`
  flex: 1;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SendButton = styled.button`
  background: ${theme.gradients.primary};
  color: #000;
  border: none;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.medium};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.button};
  }
`;

const SpinWheel = ({
  onNavigate,
  user: propUser,
  updateUserPoints: propUpdateUserPoints,
  embedded = false,
  compact = false,
  minimalText = false
}) => {
  const { user: hookUser, updateUserPoints: hookUpdateUserPoints } = useAuth();
  
  // Use props if available (from App.js), otherwise fallback to hook (standalone usage)
  const user = propUser || hookUser;
  const updateUserPoints = propUpdateUserPoints || hookUpdateUserPoints;

  const [attempts, setAttempts] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isBuyingSpins, setIsBuyingSpins] = useState(false);
  const [showBuyConfirm, setShowBuyConfirm] = useState(false);
  const [rotation, setRotation] = useState(0);
  const rotationRef = useRef(0);
  const [spinDuration, setSpinDuration] = useState(4000);
  const [winResult, setWinResult] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationClosing, setCelebrationClosing] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wheelSegments, setWheelSegments] = useState([]);
  const [wheelConfig, setWheelConfig] = useState(null);
  const [strapiUserPoints, setStrapiUserPoints] = useState(0);
  const [buyLimitInfo, setBuyLimitInfo] = useState({
    maxBuyLimit: 5,
    buyCount: 0,
    remainingBuyCount: 5,
    canBuyMoreSpins: true,
  });
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      user: 'System',
      message: 'Welcome to the Daily Spin. Good luck!',
      timestamp: new Date(),
      isSystem: true
    }
  ]);
  const [messageInput, setMessageInput] = useState('');
  const chatEndRef = useRef(null);

  const getWelcomeMessage = useCallback(() => ({
    id: 1,
    user: 'System',
    message: 'Welcome to the Daily Spin. Good luck!',
    timestamp: new Date(),
    isSystem: true
  }), []);

  const getRetentionCutoff = useCallback(
    () => new Date(Date.now() - (CHAT_MESSAGE_RETENTION_HOURS * 60 * 60 * 1000)),
    []
  );

  const normalizeMessageTimestamp = useCallback((value) => {
    if (value instanceof Date) return value;
    const parsed = new Date(value || 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const pruneExpiredMessages = useCallback((messages) => {
    const cutoff = getRetentionCutoff();
    return (Array.isArray(messages) ? messages : []).filter((message) => {
      const timestamp = normalizeMessageTimestamp(message?.timestamp);
      return !!timestamp && timestamp >= cutoff;
    });
  }, [getRetentionCutoff, normalizeMessageTimestamp]);

  const withChatRetention = useCallback((messages) => {
    const visibleMessages = pruneExpiredMessages(messages);
    return visibleMessages.length > 0 ? visibleMessages : [getWelcomeMessage()];
  }, [pruneExpiredMessages, getWelcomeMessage]);

  // Generate confetti pieces
  const generateConfetti = useCallback(() => {
    const pieces = [];
    for (let i = 0; i < 40; i++) {
      pieces.push({
        id: i,
        left: Math.random() * 100,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        size: Math.random() * 10 + 6,
        duration: Math.random() * 2 + 2,
        delay: Math.random() * 0.8,
        round: Math.random() > 0.5
      });
    }
    setConfettiPieces(pieces);
  }, []);

  // Dismiss celebration
  const dismissCelebration = useCallback(() => {
    setCelebrationClosing(true);
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationClosing(false);
      setConfettiPieces([]);
    }, 350);
  }, []);

  // Auto-dismiss celebration after 6 seconds
  useEffect(() => {
    if (showCelebration && !celebrationClosing) {
      const timer = setTimeout(() => dismissCelebration(), 6000);
      return () => clearTimeout(timer);
    }
  }, [showCelebration, celebrationClosing, dismissCelebration]);

  // Load wheel configuration and chat on mount
  useEffect(() => {
    loadWheelConfig();
    fetchStrapiUserPoints();
    loadChatHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch real-time points from Strapi
  const fetchStrapiUserPoints = async () => {
    try {
      const userData = await ApiService.getUser();
      const newPoints = userData.points || 0;
      console.log('🎰 SpinWheel: Fetched user points:', newPoints);
      setStrapiUserPoints(newPoints);
      // Sync with global user context
      if (updateUserPoints) {
        console.log('🎰 SpinWheel: Updating global user points to:', newPoints);
        updateUserPoints(newPoints);
      } else {
        console.warn('🎰 SpinWheel: updateUserPoints not available');
      }
    } catch (err) {
      console.error('Error fetching Strapi user points:', err);
    }
  };

  // Load chat history from Strapi
  const loadChatHistory = async () => {
    try {
      const cutoffIso = getRetentionCutoff().toISOString();
      const data = await ApiService.getChatMessages('spin-wheel', 1, 50, cutoffIso);
      
      // Safely map messages with proper null checks
      const formattedMessages = (data.data || [])
        .filter(msg => msg && msg.attributes) // Filter out invalid messages
        .map((msg) => {
          const attrs = msg.attributes;
          return {
            id: msg.id,
            user: attrs.user?.data?.attributes?.username || attrs.user?.username || 'System',
            message: attrs.message || '',
            timestamp: new Date(attrs.createdAt || Date.now()),
            isSystem: attrs.type === 'system',
            isWin: attrs.type === 'win'
          };
        });

      setChatMessages(withChatRetention(formattedMessages));
    } catch (err) {
      console.error('Error loading chat history:', err);
      console.log('Chat history will start fresh. This is normal if chat-message content type doesn\'t exist yet.');
      // Keep default welcome message on error
      setChatMessages([getWelcomeMessage()]);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setChatMessages((prev) => withChatRetention(prev));
    }, CHAT_PURGE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [withChatRetention]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const loadWheelConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await SpinWheelService.getWheelConfig();

      if (!data.success) {
        // Provide clearer auth vs generic error messaging
        if (data.status === 401) {
          setError('Unauthorized. Please login to access the spin wheel.');
        } else if (data.status === 403) {
          setError('Forbidden. Your account lacks permission for the spin wheel.');
        } else {
          setError(data.message || 'Failed to load configuration');
        }
        return;
      }

      // Defensive parsing
      const cfg = data.config || data.data?.config;
      const uData = data.userData || data.data?.userData;
      if (!cfg || !uData || !cfg.segments) {
        setError('Invalid configuration response from server.');
        return;
      }
      setWheelSegments(Array.isArray(cfg.segments) ? cfg.segments : []);
      setWheelConfig(cfg);
      setAttempts(uData.remainingSpins || 0);
      const maxBuyLimit = Number.isFinite(Number(uData.maxBuyLimit)) ? Number(uData.maxBuyLimit) : 5;
      const buyCount = Number.isFinite(Number(uData.buyCount)) ? Number(uData.buyCount) : 0;
      const remainingBuyCount = Number.isFinite(Number(uData.remainingBuyCount))
        ? Number(uData.remainingBuyCount)
        : Math.max(0, maxBuyLimit - buyCount);
      const canBuyMoreSpins = typeof uData.canBuyMoreSpins === 'boolean'
        ? uData.canBuyMoreSpins
        : remainingBuyCount > 0;
      setBuyLimitInfo({
        maxBuyLimit,
        buyCount,
        remainingBuyCount,
        canBuyMoreSpins,
      });
    } catch (err) {
      console.error('Error loading wheel config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const drawWheel = () => {
    const segments = wheelSegments.length;
    const anglePerSegment = 360 / segments;
    const radius = 200;
    const centerX = 250;
    const centerY = 250;

    return wheelSegments.map((segment, index) => {
      const startAngle = (index * anglePerSegment - 90) * (Math.PI / 180);
      const endAngle = ((index + 1) * anglePerSegment - 90) * (Math.PI / 180);

      const x1 = centerX + radius * Math.cos(startAngle);
      const y1 = centerY + radius * Math.sin(startAngle);
      const x2 = centerX + radius * Math.cos(endAngle);
      const y2 = centerY + radius * Math.sin(endAngle);

      const largeArcFlag = anglePerSegment > 180 ? 1 : 0;

      const pathData = `
        M ${centerX} ${centerY}
        L ${x1} ${y1}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}
        Z
      `;

      // Calculate text position and rotation
      const textRadius = radius * 0.65;
      
      // Text rotation should align with the segment direction
      const textRotation = (index * anglePerSegment + anglePerSegment / 2);

      return (
        <g key={segment.id}>
          <path
            d={pathData}
            fill={segment.color}
            stroke="#fff"
            strokeWidth="3"
          />
          <text
            x={centerX}
            y={centerY}
            fill="#fff"
            fontSize="22"
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline="middle"
            transform={`rotate(${textRotation}, ${centerX}, ${centerY}) translate(0, ${-textRadius})`}
          >
            {segment.label}
          </text>
        </g>
      );
    });
  };

  const spinWheel = async () => {
    if (attempts <= 0 || isSpinning || isBuyingSpins || !wheelSegments.length) return;

    setIsSpinning(true);
    setShowBuyConfirm(false);
    setWinResult(null);

    try {
      const data = await SpinWheelService.spin();

      if (!data.success) {
        if (data.status === 401) {
          setError('Unauthorized. Please login again.');
        } else if (data.status === 403) {
          setError('Forbidden. You cannot spin at this time.');
        } else {
          setError(data.message || 'Spin failed');
        }
        setIsSpinning(false);
        return;
      }

      const raw = data.data || {};
      if (!raw.segmentIndex && raw.segmentIndex !== 0) {
        setError('Malformed spin response');
        setIsSpinning(false);
        return;
      }
      const anglePerSegment = 360 / wheelSegments.length;

      // Physics-based spin calculation
      const duration = raw.spinDuration || 4000;
      setSpinDuration(duration);

      // Accumulate rotation from current position — no reset, no jump
      const baseSpins = 8;
      const extraSpins = Math.floor(Math.random() * 5);
      const fullSpins = baseSpins + extraSpins;

      const segmentStartAngle = -90 + (raw.segmentIndex * anglePerSegment);
      const segmentCenterAngle = segmentStartAngle + (anglePerSegment / 2);
      const segmentVariance = (Math.random() - 0.5) * anglePerSegment * 0.5;
      const targetAngle = 90 - segmentCenterAngle + segmentVariance;
      const normalizedTarget = ((targetAngle % 360) + 360) % 360;

      // Add full spins on top of current rotation for seamless continuation
      const currentBase = rotationRef.current;
      const newRotation = currentBase + (fullSpins * 360) + normalizedTarget + 180 - (currentBase % 360);
      rotationRef.current = newRotation;

      // Use rAF to ensure the browser paints the current state before transitioning
      requestAnimationFrame(() => {
        setRotation(newRotation);
      });

      // Update attempts immediately (optimistic) if present
      if (raw.remainingSpins != null) setAttempts(raw.remainingSpins);

      // Use the server-returned segmentIndex directly — no stale-closure math needed
      const serverSegmentIndex = raw.segmentIndex;

      setTimeout(async () => {
        const wonSegment = wheelSegments[serverSegmentIndex];

        // Update points from spin result (already has newTotalPoints)
        if (data.newTotalPoints != null) {
          setStrapiUserPoints(data.newTotalPoints);
          if (updateUserPoints) updateUserPoints(data.newTotalPoints);
        }

        setWinResult({
          ...wonSegment,
          pointsWon: data.pointsChange,
          newTotalPoints: data.newTotalPoints
        });

        // Trigger celebration overlay
        if (Number(data.pointsChange || 0) > 0) {
          generateConfetti();
        } else {
          setConfettiPieces([]);
        }
        setShowCelebration(true);
        setCelebrationClosing(false);

        addWinMessage(user?.username || 'Player', {
          ...wonSegment,
          points: data.pointsChange
        });

        await loadWheelConfig();
        setIsSpinning(false);
      }, duration);
    } 
     catch (err) {
      console.error('Error spinning wheel:', err);
      setIsSpinning(false);
      setError(err.message);
      
      // Show error in chat
      addSystemMessage(`Error: ${err.message}`);
    }
  };

  const addWinMessage = async (username, segment) => {
    const points = Number(segment?.points || 0);
    const messageText = points > 0
      ? `Won ${points} GGs!`
      : 'No GGs this round — better luck next time, champion! 🍀';
    
    // Optimistic local update first
    const localMessage = {
      id: Date.now(),
      user: username,
      message: messageText,
      timestamp: new Date(),
      isWin: true,
      points: segment.points
    };
    setChatMessages((prev) => withChatRetention([...prev, localMessage]));

    // Broadcast to backend so other users see it
    try {
      await ApiService.sendChatMessage({
        message: messageText,
        roomId: 'spin-wheel',
        type: 'win'
      });
    } catch (err) {
      console.log('Win broadcast failed, local entry retained:', err.message);
    }
  };

  const addSystemMessage = async (message) => {
    try {
      // const data = await ApiService.sendChatMessage({
      //   message: message,
      //   roomId: 'spin-wheel',
      //   type: 'system'
      // });
      
      // API call commented out - using local fallback
      const systemMessage = {
        id: Date.now(),
        user: 'System',
        message: message,
        timestamp: new Date(),
        isSystem: true
      };
      setChatMessages((prev) => withChatRetention([...prev, systemMessage]));
    } catch (err) {
      console.log('System message saved locally only:', err.message);
      // Fallback to local message
      const systemMessage = {
        id: Date.now(),
        user: 'System',
        message: message,
        timestamp: new Date(),
        isSystem: true
      };
      setChatMessages((prev) => withChatRetention([...prev, systemMessage]));
    }
  };

  const handleRestoreSpins = async () => {
    if (!wheelConfig || isSpinning) return;

    setIsBuyingSpins(true);
    try {
      const data = await SpinWheelService.restoreSpins();

      if (!data.success) {
        if (data.status === 401) {
          setError('Unauthorized. Please login again.');
        } else if (data.status === 403) {
          setError('Restore forbidden. Not enough permissions or balance.');
        } else if (data.buyLimitReached) {
          setError(data.message || 'Buy limit reached.');
        } else {
          setError(data.message || 'Failed to restore spins');
        }
        addSystemMessage(`Error: ${data.message || 'Restore failed'}`);
        if (data.buyLimitReached) {
          loadWheelConfig();
        }
        return;
      }

      // Immediate optimistic UI updates
      if (data.newSpinCount != null) {
        setAttempts(data.newSpinCount);
      } else if (data.spinsAdded != null) {
        setAttempts(prev => prev + data.spinsAdded);
      }
      if (data.newPointsBalance != null) {
        updateUserPoints && updateUserPoints(data.newPointsBalance);
        setStrapiUserPoints(data.newPointsBalance);
      }
      if (data.maxBuyLimit != null || data.buyCount != null || data.remainingBuyCount != null) {
        const maxBuyLimit = Number.isFinite(Number(data.maxBuyLimit)) ? Number(data.maxBuyLimit) : buyLimitInfo.maxBuyLimit;
        const buyCount = Number.isFinite(Number(data.buyCount)) ? Number(data.buyCount) : buyLimitInfo.buyCount;
        const remainingBuyCount = Number.isFinite(Number(data.remainingBuyCount))
          ? Number(data.remainingBuyCount)
          : Math.max(0, maxBuyLimit - buyCount);
        setBuyLimitInfo({
          maxBuyLimit,
          buyCount,
          remainingBuyCount,
          canBuyMoreSpins: remainingBuyCount > 0,
        });
      }
      addSystemMessage(data.message || `Spins restored (+${data.spinsAdded || wheelConfig.spinsPerRestore})`);
      setShowBuyConfirm(false);

      // Background refresh to reconcile with server authoritative state
      loadWheelConfig();
      fetchStrapiUserPoints();
    } catch (err) {
      console.error('Error restoring spins:', err);
      setError(err.message);
      addSystemMessage(`Error: ${err.message}`);
    } finally {
      setIsBuyingSpins(false);
    }
  };

  const canAffordRestore = !!wheelConfig && strapiUserPoints >= wheelConfig.restoreCost;
  const canBuyMoreSpins = !!buyLimitInfo.canBuyMoreSpins;
  const centerButtonDisabled = isSpinning || isBuyingSpins || !wheelSegments.length || (attempts <= 0 && (!canAffordRestore || !canBuyMoreSpins));
  const centerButtonMode = attempts > 0 ? 'spin' : 'buy';
  const centerButtonLabel = isSpinning
    ? (minimalText ? '...' : 'SPINNING...')
    : (attempts > 0 ? (minimalText ? 'GO' : 'SPIN') : 'BUY');
  const wonPoints = Number(winResult?.pointsWon || 0);
  const isNoWin = wonPoints <= 0;
  const celebrationTitleText = isNoWin ? 'So Close!' : 'Congratulations!';
  const celebrationSubtitleText = isNoWin
    ? 'No GGs this spin — better luck next time, champion! 🍀'
    : 'You spun and won';

  const handleCenterButtonClick = () => {
    if (centerButtonDisabled) return;
    if (attempts > 0) {
      spinWheel();
      return;
    }
    if (!canBuyMoreSpins) {
      setError(`Buy limit reached (${buyLimitInfo.buyCount}/${buyLimitInfo.maxBuyLimit}).`);
      return;
    }
    setShowBuyConfirm(true);
  };

  const sendMessage = async () => {
    if (!messageInput.trim()) return;

    const messageText = messageInput;
    setMessageInput(''); // Clear input immediately for better UX

    try {
      // const data = await ApiService.sendChatMessage({
      //   message: messageText,
      //   roomId: 'spin-wheel',
      //   type: 'user'
      // });
      
      // API call commented out - using local fallback
      const newMessage = {
        id: Date.now(),
        user: user?.username || 'Guest',
        message: messageText,
        timestamp: new Date(),
        isWin: false,
        isSystem: false
      };
      setChatMessages((prev) => withChatRetention([...prev, newMessage]));
    } catch (err) {
      console.log('Chat message will only be stored locally:', err.message);
      // Optimistically add message even if save fails
      const newMessage = {
        id: Date.now(),
        user: user?.username || 'Guest',
        message: messageText,
        timestamp: new Date(),
        isWin: false,
        isSystem: false
      };
      setChatMessages((prev) => withChatRetention([...prev, newMessage]));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const formatTime = (date) => {
    const parsedDate = date instanceof Date ? date : new Date(date || Date.now());
    if (Number.isNaN(parsedDate.getTime())) return '--:--';
    return parsedDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <SpinWheelContainer $embedded={embedded}>
        <ContentWrapper $embedded={embedded}>
          <div style={{ textAlign: 'center', padding: '50px', color: '#fff' }}>
            <h2>Loading Spin Wheel...</h2>
          </div>
        </ContentWrapper>
      </SpinWheelContainer>
    );
  }

  if (error && !wheelSegments.length) {
    return (
      <SpinWheelContainer $embedded={embedded}>
        <ContentWrapper $embedded={embedded}>
          <div style={{ textAlign: 'center', padding: '50px', color: '#fff' }}>
            <h2>Error Loading Wheel</h2>
            <p>{error}</p>
            <button onClick={loadWheelConfig} style={{ marginTop: '20px', padding: '10px 20px' }}>
              Retry
            </button>
          </div>
        </ContentWrapper>
      </SpinWheelContainer>
    );
  }

  return (
    <>
    <SpinWheelContainer $embedded={embedded}>
      <ContentWrapper $embedded={embedded}>
        <MainLayout $embedded={embedded}>
          <LeftSection>
            <WheelSection $embedded={embedded} $compact={compact}>
              <WheelTitle $compact={compact}>Daily Spin Wheel</WheelTitle>
              <WheelSubtitle $compact={compact}>Spin to win rewards.</WheelSubtitle>

              <AttemptsInfo $compact={compact}>
                <AttemptsLabel>
                  Attempts Remaining:
                  <AttemptsCount>{attempts}</AttemptsCount>
                </AttemptsLabel>
                <AttemptsLabel style={{ fontSize: '14px', marginTop: '10px' }}>
                Current Balance: <AttemptsCount>{strapiUserPoints}</AttemptsCount><img src='images/ggcoin.svg' alt='GG Logo'  style={{width:"24px", height:"24px"}}/>
                </AttemptsLabel>
                <AttemptsLabel style={{ fontSize: '14px', marginTop: '10px' }}>
                  Buy Limit: <AttemptsCount>{buyLimitInfo.remainingBuyCount}</AttemptsCount> left
                </AttemptsLabel>
              </AttemptsInfo>

              {wheelSegments.length > 0 && (
                <WheelContainer $compact={compact}>
                  {/* Fixed needle at top - like Wheel of Fortune */}
                  <NeedleBracket />
                  <WheelPointer />
                  
                  {/* Rotating wheel */}
                  <WheelCanvas
                    viewBox="0 0 500 500"
                    $isSpinning={isSpinning}
                    $rotation={rotation}
                    $duration={spinDuration}
                  >
                    {drawWheel()}
                  </WheelCanvas>
                  
                  {/* Center button to spin */}
                  <CenterCircle
                    $disabled={centerButtonDisabled}
                    $mode={centerButtonMode}
                    $isSpinning={isSpinning}
                    $compact={compact}
                    onClick={handleCenterButtonClick}
                  >
                    {centerButtonLabel}
                  </CenterCircle>
                </WheelContainer>
              )}

              {/* Winner Announcements Log */}
              {chatMessages.filter(m => m.isWin).length > 0 && (
                <WinnerLogWrap>
                  <WinnerLogTitle>Recent Winners</WinnerLogTitle>
                  {chatMessages.filter(m => m.isWin).slice(-8).reverse().map(msg => (
                    <WinnerLogEntry key={msg.id}>
                      <WinnerLogDot />
                      <WinnerLogName>{msg.user}</WinnerLogName>
                      <WinnerLogText>{msg.message}</WinnerLogText>
                      <WinnerLogTime>{formatTime(msg.timestamp)}</WinnerLogTime>
                    </WinnerLogEntry>
                  ))}
                </WinnerLogWrap>
              )}

              {error && (
                <div style={{ color: '#ff6b6b', marginTop: '10px', padding: '10px', background: 'rgba(255,107,107,0.1)', borderRadius: '8px' }}>
                  {error}
                </div>
              )}
            </WheelSection>
          </LeftSection>

          {!embedded && (
            <ChatSection>
              <ChatTitle>
                Live Chat
              </ChatTitle>

              <ChatMessages>
                {chatMessages.map((msg) => (
                  <ChatMessage key={msg.id} $isWin={msg.isWin}>
                    <MessageHeader>
                      <MessageUser>{msg.user}</MessageUser>
                      <MessageTime>{formatTime(msg.timestamp)}</MessageTime>
                    </MessageHeader>
                    <MessageContent>
                      {msg.isWin ? (
                        <WinAnnouncement>
                          {msg.message}
                        </WinAnnouncement>
                      ) : (
                        msg.message
                      )}
                    </MessageContent>
                  </ChatMessage>
                ))}
                <div ref={chatEndRef} />
              </ChatMessages>

              <ChatInputContainer>
                <ChatInput
                  type="text"
                  placeholder="Type your message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <SendButton onClick={sendMessage}>Send</SendButton>
              </ChatInputContainer>
            </ChatSection>
          )}
        </MainLayout>
      </ContentWrapper>
    </SpinWheelContainer>

    {/* Buy confirmation modal — centered on screen */}
    {showBuyConfirm && attempts <= 0 && wheelConfig && (
      <>
        <BuyConfirmOverlay onClick={() => !isBuyingSpins && setShowBuyConfirm(false)} />
        <BuyConfirmCard>
          <BuyConfirmTitle>Buy More Spins?</BuyConfirmTitle>
          <BuyConfirmText>
            Exchange {wheelConfig.restoreCost} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} />
            {' '}for {wheelConfig.spinsPerRestore} new spin{wheelConfig.spinsPerRestore > 1 ? 's' : ''}.
            {' '}({buyLimitInfo.remainingBuyCount} buy{buyLimitInfo.remainingBuyCount !== 1 ? 's' : ''} left)
          </BuyConfirmText>
          <BuyConfirmActions>
            <BuyConfirmButton type="button" onClick={() => setShowBuyConfirm(false)} disabled={isBuyingSpins}>
              Cancel
            </BuyConfirmButton>
            <BuyConfirmButton
              type="button"
              $variant="confirm"
              onClick={handleRestoreSpins}
              disabled={isBuyingSpins || !canAffordRestore || !canBuyMoreSpins}
            >
              {isBuyingSpins ? 'Buying...' : 'Confirm Buy'}
            </BuyConfirmButton>
          </BuyConfirmActions>
        </BuyConfirmCard>
      </>
    )}

    {/* Full-screen celebration overlay */}
    {showCelebration && winResult && (
      <>
        {!isNoWin && confettiPieces.map(p => (
          <ConfettiPiece
            key={p.id}
            $left={p.left}
            $color={p.color}
            $size={p.size}
            $duration={p.duration}
            $delay={p.delay}
            $round={p.round}
          />
        ))}
        <CelebrationBackdrop $closing={celebrationClosing} onClick={dismissCelebration}>
          <CelebrationCard onClick={e => e.stopPropagation()}>
            {!isNoWin && (
              <FireworkBurstArea>
                {/* Expanding rings */}
                <BurstRing $color="rgba(255, 215, 0, 0.6)" $delay="0.3s" />
                <BurstRing $color="rgba(255, 107, 107, 0.4)" $delay="0.5s" />
                <BurstRing $color="rgba(78, 205, 196, 0.3)" $delay="0.7s" />

                {/* Burst particles */}
                {BURST_PARTICLES.map((p, i) => (
                  <SparkleParticle key={i} $tx={p.tx} $ty={p.ty} $color={p.color} $size={p.size} $dur={p.dur} $delay={p.delay} />
                ))}

                {/* Cross sparkles scattered around */}
                <SparkleIcon $top="10%" $left="15%"  $color="#FFD700" $delay="0.4s" $dur="1.4s" />
                <SparkleIcon $top="20%" $left="78%"  $color="#FF6B6B" $delay="0.6s" $dur="1.2s" />
                <SparkleIcon $top="70%" $left="12%"  $color="#4ECDC4" $delay="0.8s" $dur="1.5s" />
                <SparkleIcon $top="75%" $left="82%"  $color="#FFEAA7" $delay="0.5s" $dur="1.3s" />
                <SparkleIcon $top="8%"  $left="55%"  $color="#DDA0DD" $delay="0.7s" $dur="1.1s" />
                <SparkleIcon $top="85%" $left="50%"  $color="#FF8C00" $delay="0.9s" $dur="1.4s" />

                {/* Firework trail lines */}
                <FireworkTrailLine $left="30%" $color="#FFD700" $delay="0s" />
                <FireworkTrailLine $left="70%" $color="#FF6B6B" $delay="0.15s" />
                <FireworkTrailLine $left="50%" $color="#4ECDC4" $delay="0.3s" />

                {/* Firework starbursts */}
                <FireworkStarburst $top="25%" $left="20%" $color="rgba(255, 107, 107, 0.35)" $delay="0.5s" />
                <FireworkStarburst $top="30%" $left="80%" $color="rgba(255, 215, 0, 0.35)" $delay="0.7s" />
                <FireworkStarburst $top="70%" $left="35%" $color="rgba(78, 205, 196, 0.3)" $delay="0.9s" />

                {/* Center icon */}
                <CenterStar></CenterStar>
              </FireworkBurstArea>
            )}
            <CelebrationTitle>{celebrationTitleText}</CelebrationTitle>
            <CelebrationSubtitle>{celebrationSubtitleText}</CelebrationSubtitle>
            <CelebrationAmount>
              {isNoWin ? (
                <span>Better luck next time!</span>
              ) : (
                <>
                  <span>+{wonPoints}</span> <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '28px', height: '28px', verticalAlign: 'middle' }} />
                </>
              )}
            </CelebrationAmount>
            <CelebrationBalance>
              New Balance: {strapiUserPoints} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '20px', height: '20px', verticalAlign: 'middle' }} />
            </CelebrationBalance>
            <CelebrationDismiss onClick={dismissCelebration}>
              Continue
            </CelebrationDismiss>
          </CelebrationCard>
        </CelebrationBackdrop>
      </>
    )}
    </>
  );
};

export default SpinWheel;
